'use client';

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useAuthMethodTracking } from '@/lib/stores/auth-tracking';

type OAuthProviderKey = 'google' | 'microsoft';

const PROVIDERS: Record<
  OAuthProviderKey,
  {
    slug: 'google' | 'azure';
    label: string;
    scopes?: string;
    icon: React.ReactNode;
  }
> = {
  google: {
    slug: 'google',
    label: 'Continue with Google',
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
    ),
  },
  microsoft: {
    slug: 'azure',
    label: 'Continue with Microsoft',
    scopes: 'openid profile email',
    icon: (
      <svg viewBox="0 0 21 21" className="h-4 w-4" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
      </svg>
    ),
  },
};

interface OAuthSignInProps {
  provider: OAuthProviderKey;
  returnUrl?: string;
  invitationToken?: string;
  onBeforeSignIn?: () => boolean;
}

export function OAuthSignIn({
  provider,
  returnUrl,
  invitationToken,
  onBeforeSignIn,
}: OAuthSignInProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { wasLastMethod, markAsUsed } = useAuthMethodTracking(provider);
  const config = PROVIDERS[provider];

  const handleSignIn = async () => {
    if (onBeforeSignIn && !onBeforeSignIn()) {
      return;
    }

    setIsLoading(true);

    const params = new URLSearchParams();
    if (invitationToken) {
      params.set('returnUrl', `/invitation?token=${invitationToken}`);
      params.set('invitation_token', invitationToken);
    } else if (returnUrl) {
      params.set('returnUrl', returnUrl);
    }
    const callbackUrl = `${window.location.origin}/auth/callback${
      params.toString() ? `?${params.toString()}` : ''
    }`;

    const supabase = createClient();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: config.slug,
      options: {
        redirectTo: callbackUrl,
        ...(config.scopes ? { scopes: config.scopes } : {}),
      },
    });

    if (error) {
      toast.error(`${config.label.replace('Continue with ', '')} sign-in failed`, {
        description: error.message,
        duration: 5000,
      });
      setIsLoading(false);
      return;
    }

    markAsUsed();
    // On success the browser redirects to the provider's login page.
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleSignIn}
        disabled={isLoading}
        className="w-full h-10 flex items-center justify-center gap-2 rounded-lg border border-border bg-background hover:bg-accent transition-colors text-sm font-medium disabled:opacity-60"
      >
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : config.icon}
        {config.label}
      </button>
      {wasLastMethod && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-background shadow-sm">
          <div className="w-full h-full bg-green-500 rounded-full animate-pulse" />
        </div>
      )}
    </div>
  );
}
